import { useEffect, useState } from 'react'

interface ToastProps {
  message: string
  isVisible: boolean
  onHide: () => void
  duration?: number
  /** Optional button inside the toast — an undo, typically */
  actionLabel?: string
  onAction?: () => void
}

export function Toast({ message, isVisible, onHide, duration = 2000, actionLabel, onAction }: ToastProps) {
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (isVisible) {
      // Next frame, not synchronously: the toast mounts hidden first so the
      // enter transition actually animates
      const raf = requestAnimationFrame(() => setShow(true))
      const timer = setTimeout(() => {
        setShow(false)
        setTimeout(onHide, 200)
      }, duration)
      return () => {
        cancelAnimationFrame(raf)
        clearTimeout(timer)
      }
    }
  }, [isVisible, duration, onHide])

  if (!isVisible && !show) return null

  return (
    <div
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-2.5 bg-text text-surface text-sm font-medium rounded-lg shadow-lg transition-all duration-200 ${
        show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
      }`}
    >
      {message}
      {actionLabel && onAction && (
        <button
          onClick={() => {
            onAction()
            setShow(false)
            setTimeout(onHide, 200)
          }}
          className="underline underline-offset-2 hover:no-underline cursor-pointer"
        >
          {actionLabel}
        </button>
      )}
    </div>
  )
}
